import { v4 as uuidv4 } from "uuid"; // npm install uuid
import { DocumentContent } from "../utils/wordContextExtractor";
import { PipelineResponse } from "./types";
import { runPipeline } from "./pipelineService";
import { escapeHtmlForDocument } from "../utils/replaceStatements";

let documentId = uuidv4(); // Generate once per document

// Export documentId getter
export function getDocumentId(): string {
  return documentId;
}

export async function sendPageToBackend(
  pageNumber: number,
  query: string
): Promise<PipelineResponse> {
  try {
    const result = await Word.run(async (context: any) => {
      const document = context.document;
      const docProperties = document.properties;
      docProperties.load("title, author");

      const bodyRange = document.body.getRange();
      const pages = bodyRange.pages;
      pages.load();
      await context.sync();

      for (let i = 0; i < pages.items.length; i++) {
        pages.items[i].load("index");
      }
      await context.sync();

      const totalPages = pages.items.length;

      const targetPage = pages.items.find((p: any) => p.index === pageNumber);
      if (!targetPage) {
        throw new Error(`Page ${pageNumber} not found.`);
      }

      const pageRange = targetPage.getRange(Word.RangeLocation.whole);
      pageRange.load("text, paragraphs, font, style");
      const paragraphs = pageRange.paragraphs;
      paragraphs.load("text, font, style, alignment");
      await context.sync();

      const textContent: string = pageRange.text || "";

      const styles: any[] = [];
      for (const para of paragraphs.items) {
        const paraFont = para.font;
        paraFont.load("name, size, bold, italic, underline");
        await context.sync();

        styles.push({
          text: para.text,
          fontName: paraFont.name,
          fontSize: paraFont.size,
          bold: paraFont.bold,
          italic: paraFont.italic,
          underline: paraFont.underline,
          alignment: para.alignment,
          style: para.style,
        });
      }

      let htmlContent = "";
      for (const style of styles) {
        const escapedText = escapeHtmlForDocument(style.text);
        let styleAttr = "";
        if (style.fontName) styleAttr += `font-family: ${style.fontName}; `;
        if (style.fontSize) styleAttr += `font-size: ${style.fontSize}pt; `;
        if (style.bold) styleAttr += `font-weight: bold; `;
        if (style.italic) styleAttr += `font-style: italic; `;
        if (style.underline && style.underline !== "None") styleAttr += `text-decoration: underline; `;
        if (style.alignment) styleAttr += `text-align: ${style.alignment.toLowerCase()}; `;
        const className = style.style ? style.style.replace(/\s+/g, "-").toLowerCase() : "normal";
        htmlContent += `<p class="${className}"${styleAttr ? ` style="${styleAttr.trim()}"` : ""}>${escapedText}</p>`;
      }

      const metadata = {
        title: docProperties.title || `Page ${pageNumber}`,
        author: docProperties.author || "",
      };

      const docContent: DocumentContent = {
        html: htmlContent,
        metadata,
      };

      return { docContent, totalPages };
    });

    const pagesArray = [pageNumber];
    const response = await runPipeline(query, result.docContent, documentId, undefined, pageNumber, pagesArray, undefined, result.totalPages, false);
    return response;
  } catch (err) {
    console.error("sendPageToBackend failed:", err);
    return { success: false, message: err instanceof Error ? err.message : String(err) };
  }
}

export async function sendDetailedCheckToBackend(
  pageNumber: number,
  subpart: string
): Promise<PipelineResponse> {
  try {
    const result = await Word.run(async (context: any) => {
      const document = context.document;
      const bodyRange = document.body.getRange();
      const pages = bodyRange.pages;
      pages.load();
      await context.sync();

      for (let i = 0; i < pages.items.length; i++) {
        pages.items[i].load("index");
      }
      await context.sync();

      const targetPage = pages.items.find((p: any) => p.index === pageNumber);
      if (!targetPage) throw new Error(`Page ${pageNumber} not found.`);

      const pageRange = targetPage.getRange(Word.RangeLocation.whole);
      pageRange.load("text");
      await context.sync();

      return pageRange.text || "";
    });

    const pagesArray = [pageNumber];
    const payload = {
      action: "detailed_check",
      pageNumber,
      subpart,
      pages: pagesArray,
      timestamp: new Date().toISOString()
    };
    
    const docContent: DocumentContent = {
      html: `<pre>${result}</pre>`,
      metadata: { title: `Page ${pageNumber}`, author: "" }
    };

    const response = await runPipeline(
      JSON.stringify(payload),
      docContent,
      documentId,
      undefined,
      pageNumber,
      pagesArray,
      subpart,
      undefined,
      true
    );
    
    return response;
  } catch (err) {
    console.error("sendDetailedCheckToBackend failed:", err);
    return { success: false, message: err instanceof Error ? err.message : String(err) };
  }
}

export async function sendExportSummaryToBackend(): Promise<PipelineResponse> {
  try {
    const totalPages = await Word.run(async (context: any) => {
      const bodyRange = context.document.body.getRange();
      const pages = bodyRange.pages;
      pages.load();
      await context.sync();
      return pages.items.length;
    });

    const pagesArray = Array.from({ length: totalPages }, (_, i) => i + 1);

    const payload = {
      action: "export_summary",
      pages: pagesArray,
      timestamp: new Date().toISOString()
    };
    
    console.log("📦 Sending payload to backend:", payload);

    const response = await runPipeline(
      JSON.stringify(payload),
      undefined,
      documentId,
      undefined,
      undefined,
      pagesArray,
      undefined,
      totalPages,
      true
    );
   
    console.log("📬 Response comments_data:", response.comments_data);
    return response;
  } catch (err) {
    console.error("sendExportSummaryToBackend failed:", err);
    return { success: false, message: err instanceof Error ? err.message : String(err) };
  }
}

export async function sendWholePageToBackend(query: string): Promise<PipelineResponse> {
  return sendPageToBackend(1, query);
}

export async function sendWholeDocumentToBackend(query: string): Promise<PipelineResponse> {
  try {
    const result = await Word.run(async (context: any) => {
      const document = context.document;

      const docProperties = document.properties;
      docProperties.load("title, author");

      const bodyRange = document.body.getRange(Word.RangeLocation.whole);

      bodyRange.load("text");
      const ooxmlResult = bodyRange.getOoxml();
      await context.sync();

      const textContent: string = bodyRange.text || "";
      const ooxmlContent: string | undefined =
        ooxmlResult && ooxmlResult.value ? ooxmlResult.value : undefined;

      function escapeHtml(s: string) {
        return escapeHtmlForDocument(s);
      }

      const htmlContent = `<pre style="white-space: pre-wrap; font-family: inherit;">${escapeHtml(textContent)}</pre>`;

      const metadata = {
        title: docProperties.title || "Document",
        author: docProperties.author || "",
      };

      const styles: any[] = [];

      // Load pages to get totalPages
      const pageBodyRange = document.body.getRange();
      const pages = pageBodyRange.pages;
      pages.load();
      await context.sync();

      for (let i = 0; i < pages.items.length; i++) {
        pages.items[i].load("index");
      }
      await context.sync();

      const totalPages = pages.items.length;

      const docContent: DocumentContent = {
        html: htmlContent,
        metadata,
      };

      return { docContent, totalPages };
    });

    const pagesArray = Array.from({ length: result.totalPages }, (_, i) => i + 1);
    const response = await runPipeline(query, result.docContent, undefined, undefined, undefined, pagesArray, undefined, result.totalPages, true);
    return response;
  } catch (err) {
    console.error("sendWholeDocumentToBackend failed:", err);
    return { success: false, message: err instanceof Error ? err.message : String(err) };
  }
}
